import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageTitle from "./common/PageTitle";
import { apiService } from "../api/services/apiService";
import { EntityStatus, OperationalErrorCategory } from "../types";
import { resolveReturnDestination } from "../utils/navigationReturn";

interface AddOperationalErrorProps {
  tenantId: string | null;
  returnPath?: string;
  returnSearch?: string;
}

const CATEGORIES = Object.values(OperationalErrorCategory);

const AddOperationalError: React.FC<AddOperationalErrorProps> = ({
  tenantId,
  returnPath,
  returnSearch,
}) => {
  const navigate = useNavigate();
  const goBack = () =>
    navigate(resolveReturnDestination(returnPath, returnSearch, "apis"));

  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [description, setDescription] = useState("");
  const [remediation, setRemediation] = useState("");
  const [category, setCategory] = useState<OperationalErrorCategory | "">("");
  const [httpStatus, setHttpStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setErrorMessage(null);
    try {
      await apiService.createOperationalError({
        tenantId,
        status: EntityStatus.ACTIVE,
        name: code || message || "Operational error",
        code: code || undefined,
        message: message || undefined,
        description: description || undefined,
        remediation: remediation || undefined,
        category: category || undefined,
        httpStatus: httpStatus ? Number(httpStatus) : undefined,
      });
      setSuccessOpen(true);
      setTimeout(goBack, 800);
    } catch (err: unknown) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to create operational error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageTitle
        pageTitle="Add operational error"
        pageDescription="Add a catalog entry for production support."
      />

      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Paper
          sx={{
            mt: 3,
            mb: 3,
            p: 3,
            maxWidth: "80%",
            border: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              fullWidth
            />
            <TextField
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
            />
            <TextField
              label="Description"
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />
            <TextField
              label="Remediation"
              multiline
              minRows={2}
              value={remediation}
              onChange={(e) => setRemediation(e.target.value)}
              fullWidth
            />
            <TextField
              label="HTTP status"
              type="number"
              value={httpStatus}
              onChange={(e) => setHttpStatus(e.target.value)}
              fullWidth
            />
            <TextField
              select
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value as OperationalErrorCategory)}
              fullWidth
            >
              <MenuItem value="">—</MenuItem>
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {saving ? "Saving..." : "Add operational error"}
              </Button>
              <Button
                variant="outlined"
                onClick={goBack}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Snackbar open={successOpen} autoHideDuration={2000}>
        <Alert severity="success">Operational error created successfully</Alert>
      </Snackbar>
    </Box>
  );
};

export default AddOperationalError;
